import { computed } from '@ember/object';
import { isPresent } from '@ember/utils';
import { scheduleOnce } from '@ember/runloop';
import { TIN_REGEX, TIN_LAST_FOUR_REGEX } from '@square/ember-prism/utils/regexes';
// eslint-disable-next-line import/default
import FieldKit from 'field-kit';
import InputText from './input-text';

// Removes formatting field-kit applies
export const TIN_NORMALIZE_REGEX = /-/g;
const NUMBER_DIGITS_PATTERN = /^\d*$/;
function onlyNumbers(input) {
  if (input === undefined || input === null) {
    return null;
  }
  if (typeof input !== 'string') {
    input = input.toString();
  }
  return input.replace(/\D/g, '');
}
class NumberFormatter extends FieldKit.Formatter {
  format(value) {
    if (typeof value === 'number') {
      return value.toString();
    }
    if (typeof value === 'string') {
      const valueAsNumber = Number.parseInt(value, 10);
      if (!Number.isNaN(valueAsNumber)) {
        return valueAsNumber.toString();
      }
    }
    return '';
  }
  parse(string) {
    return string ? Number(string) : null;
  }
  isChangeValid(change, error) {
    if (NUMBER_DIGITS_PATTERN.test(change.inserted.text)) {
      return super.isChangeValid(change, error);
    } else {
      return false;
    }
  }
}
class ExtendedSsnFormatter extends FieldKit.SocialSecurityNumberFormatter {
  isChangeValid(change, error) {
    if (change.inserted.text.length > 1) {
      if (onlyNumbers(change.proposed.text).length > 9) {
        change.proposed.text = onlyNumbers(change.proposed.text).slice(0, 9);
      } else {
        change.proposed.text = onlyNumbers(change.proposed.text);
      }
      change.recomputeDiff();
    }
    return super.isChangeValid(change, error);
  }
}
class ExtendedTinFormatter extends FieldKit.EmployerIdentificationNumberFormatter {
  isChangeValid(change, error) {
    if (change.inserted.text.length > 1) {
      if (onlyNumbers(change.proposed.text).length > 9) {
        // if we paste something that will overflow the max length, use the
        // beginning of the pasted numbers, rather than the end
        change.proposed.text = onlyNumbers(change.proposed.text).slice(0, 9);
      } else {
        change.proposed.text = onlyNumbers(change.proposed.text);
      }
      change.recomputeDiff();
    }
    return super.isChangeValid(change, error);
  }
}
export function maskTin(tin, isSsn, isEin) {
  // If the value entered is not a full TIN then we will not mask the value.
  // This will allow the user to see and fix the error on submit.
  if (isPresent(tin) && TIN_REGEX.test(tin)) {
    return maskTinLast4(tin.slice(-4), isSsn, isEin);
  } else {
    return tin;
  }
}
export function maskTinLast4(last4, isSsn, isEin) {
  if (isPresent(last4) && TIN_LAST_FOUR_REGEX.test(last4)) {
    if (isSsn) {
      // Special formatting for SSNs
      return `••• •• ${last4}`;
    } else if (isEin) {
      return `•• •••${last4}`;
    } else {
      return `•••••${last4}`;
    }
  } else {
    return last4;
  }
}
export function normalizeTin(tin) {
  if (isPresent(tin)) {
    return tin.replace(TIN_NORMALIZE_REGEX, '');
  } else {
    return tin;
  }
}

/**
 * This component renders a text input with TIN/SSN formatting. It will
 * automatically mask & unmask the value on blur & focus.
 * It is a extension of `<Prism::InputText />` and supports all the same properties.
 *
 * @example
 * ```
 * <Prism::InputTin
 *   @isSsn={{true}}
 *   @prefillLast4="1234"
 *   @value={{this.nameOfValueProperty}}
 *   @label="Social Security Number"
 *   @onChange={{action (mut nameOfValueProperty))}}
 * />
 * ```
 *
 */
export default InputText.extend({
  /**
   * Enables SSN formatting (XXX-XX-XXXX).
   *
   * @argument isSsn
   * @type {Boolean}
   */
  isSsn: false,
  /**
   * Enables EIN formatting (XX-XXXXXXX).
   *
   * @argument isEin
   * @type {Boolean}
   */
  isEin: false,
  /**
   * Optional passed last 4 digits of a TIN. If this is passed the initial
   * value of the input will be the last 4 when not focused and empty when
   * focused.
   *
   * Touching the field will clear the last4.
   *
   * @argument prefilledLast4
   * @type {String}
   */
  prefilledLast4: null,
  _isTouched: false,
  init(...args) {
    this._super(...args);
    this._unsetInvalidLast4();
  },
  _unsetInvalidLast4() {
    const last4 = this.prefilledLast4;
    if (!isPresent(last4) || !TIN_LAST_FOUR_REGEX.test(last4)) {
      this.set('prefilledLast4', null);
    }
  },
  _unmask() {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    let unmaskedValue = this._unmaskedValue;

    // Normalize & force a reformat to ensure the unmasked value is display
    // ready.
    unmaskedValue = this._format(normalizeTin(unmaskedValue));
    this.set('_internalValue', unmaskedValue);
  },
  _mask() {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    const internalValue = this._internalValue;
    this.set('_unmaskedValue', internalValue);
    if (!this._isTouched && this.prefilledLast4) {
      this.set('_internalValue', maskTinLast4(this.prefilledLast4, this.isSsn, this.isEin));
    } else {
      this.set('_internalValue', maskTin(internalValue, this.isSsn, this.isEin));
    }
  },
  _format(string) {
    if (this.formatter) {
      return this.formatter.format(string);
    } else {
      return string;
    }
  },
  formatter: computed('isSsn', 'isEin', function () {
    if (this.isSsn) {
      return new ExtendedSsnFormatter();
    } else if (this.isEin) {
      return new ExtendedTinFormatter();
    } else {
      return new NumberFormatter();
    }
  }),
  inputType: 'tel',
  maxlength: computed('isEin', 'isSsn', function () {
    if (!this.isSsn && !this.isEin) {
      return 9;
    } else {
      return null;
    }
  }),
  actions: {
    /**
     * @override
     */
    _onChange(event) {
      const value = event.detail.value;

      // Update our internal representation for masking/unmasking.
      this.set('_internalValue', value);
      this.set('_isTouched', true);
      if (this.onChange) {
        this.onChange(normalizeTin(value));
      }
    },
    /**
     * @override
     */
    _inputDidLoad(event) {
      this._super(event);
      const input = event.detail.input;
      input.addEventListener('focus', () => {
        scheduleOnce('afterRender', this, this._unmask);
      });
      input.addEventListener('blur', () => {
        scheduleOnce('afterRender', this, this._mask);
      });
      this._mask();
    }
  }
});